import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '../auth';
import {
  getPortalEventPhoneReservations,
  PortalApiError,
  releasePortalPhoneBooking,
  updatePortalEventPhoneReservations,
} from '../services/portalApi';
import type {
  EventPhoneReservationsResponse,
  PortalPhoneChoice,
} from '../types/api';
import { CompactTable } from './CompactTable';

interface EventPhoneReservationsPanelProps {
  eventId: string;
}

type PhoneSortKey = 'number' | 'email';
type SortDirection = 'asc' | 'desc';

export function EventPhoneReservationsPanel({
  eventId,
}: EventPhoneReservationsPanelProps) {
  const { getAccessToken } = useAuth();
  const [details, setDetails] = useState<EventPhoneReservationsResponse | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortKey, setSortKey] = useState<PhoneSortKey>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isBusy, setIsBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const response = await getPortalEventPhoneReservations(token, eventId);
        if (!cancelled) {
          setDetails(response);
          setSelected(response.Reservations.map((item) => item.PhoneNumber));
        }
      } catch (error) {
        if (!cancelled) {
          setMessage(toMessage(error));
          setIsSuccess(false);
        }
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, getAccessToken]);

  const sortedTelephones = useMemo(
    () => sortTelephones(details?.Telephones ?? [], sortKey, sortDirection),
    [details?.Telephones, sortDirection, sortKey],
  );
  const selectableNumbers = useMemo(
    () => (details?.Telephones ?? [])
      .filter((telephone) => telephone.IsAvailable || telephone.IsReserved)
      .map((telephone) => telephone.PhoneNumber),
    [details?.Telephones],
  );
  const allSelected = selectableNumbers.length > 0 &&
    selectableNumbers.every((number) => selected.includes(number));

  function toggle(phoneNumber: string): void {
    setSelected((current) => current.includes(phoneNumber)
      ? current.filter((number) => number !== phoneNumber)
      : [...current, phoneNumber]);
    setIsSuccess(false);
    setMessage(null);
  }

  function toggleAll(): void {
    setSelected((current) => {
      if (allSelected) {
        return current.filter((number) => !selectableNumbers.includes(number));
      }
      return Array.from(new Set([...current, ...selectableNumbers]));
    });
    setIsSuccess(false);
    setMessage(null);
  }

  function changeSort(key: PhoneSortKey): void {
    if (sortKey === key) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  }

  async function save(): Promise<void> {
    setIsBusy(true);
    setMessage(null);
    setIsSuccess(false);
    try {
      const token = await getAccessToken();
      const response = await updatePortalEventPhoneReservations(
        token,
        eventId,
        selected,
      );
      setDetails(response);
      setSelected(response.Reservations.map((item) => item.PhoneNumber));
      setMessage('Telephone reservations saved.');
      setIsSuccess(true);
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function releaseBooking(
    bookingId: string,
    userName: string,
  ): Promise<void> {
    if (!window.confirm(`Release the booking for ${userName}?`)) return;

    setIsBusy(true);
    setMessage(null);
    setIsSuccess(false);
    try {
      const token = await getAccessToken();
      await releasePortalPhoneBooking(token, bookingId);
      setDetails((current) => current ? {
        ...current,
        ActiveBookings: current.ActiveBookings.filter(
          (booking) => booking.BookingId !== bookingId,
        ),
      } : current);
      setMessage('The telephone booking has been released.');
      setIsSuccess(true);
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <section className="event-phone-section">
      <button className="event-phone-toggle" type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((current) => !current)}>
        <span>
          <span className="eyebrow">Phonero resources</span>
          <strong>Reserved phones</strong>
          <small>
            {selected.length} {selected.length === 1 ? 'telephone' : 'telephones'} selected
          </small>
        </span>
        <span className={'event-phone-chevron' +
          (isExpanded ? ' event-phone-chevron-open' : '')}
          aria-hidden="true">⌄</span>
      </button>

      {isExpanded ? (
        <div className="event-phone-config">
          <p>
            Participants receive a random available telephone from this list.
          </p>

          {details ? (
            <>
              <div className="phone-list-toolbar">
                <label className="phone-select-all">
                  <input type="checkbox" checked={allSelected}
                    disabled={isBusy || !details.Access.CanManageReservations || selectableNumbers.length === 0}
                    onChange={toggleAll} />
                  Select all
                </label>
                <div className="phone-sort-actions" aria-label="Sort telephones">
                  <span>Sort by:</span>
                  <button type="button" onClick={() => changeSort('number')}>
                    Number {sortIndicator(sortKey === 'number', sortDirection)}
                  </button>
                  <button type="button" onClick={() => changeSort('email')}>
                    Email {sortIndicator(sortKey === 'email', sortDirection)}
                  </button>
                </div>
              </div>

              <div className="phone-choice-list">
                {sortedTelephones.map((telephone) => {
                  const checked = selected.includes(telephone.PhoneNumber);
                  const disabled = !details.Access.CanManageReservations ||
                    (!telephone.IsAvailable && !telephone.IsReserved);
                  return (
                    <label className={'phone-choice' +
                      (disabled ? ' phone-choice-disabled' : '')}
                      key={telephone.PhoneNumber}>
                      <input type="checkbox" checked={checked}
                        disabled={disabled || isBusy}
                        onChange={() => toggle(telephone.PhoneNumber)} />
                      <strong>{telephone.PhoneNumber}</strong>
                      <span>{telephone.Email}</span>
                      <small>{telephone.UnavailableReason ?? ''}</small>
                    </label>
                  );
                })}
                {details.Telephones.length === 0 ? (
                  <p>No Phonero telephones have been registered.</p>
                ) : null}
              </div>

              <button className="primary-button" type="button"
                disabled={isBusy || !details.Access.CanManageReservations} onClick={() => void save()}>
                {isBusy ? 'Saving…' : 'Save reservations'}
              </button>
            </>
          ) : null}

          {isBusy && !details ? <p>Loading telephone reservations…</p> : null}
          {message ? (
            <p className={isSuccess ? 'success-message' : 'api-message'}
              role="status">{message}</p>
          ) : null}
        </div>
      ) : null}

      <div className="event-bookings-list">
        <h3>Active bookings</h3>
        {details?.ActiveBookings.length ? (
          <CompactTable
            label="Active event bookings"
            headers={[
              { key: 'user', label: 'User' },
              { key: 'phone', label: 'Phone number' },
              { key: 'start', label: 'Started' },
              { key: 'end', label: 'Ends' },
              { key: 'actions', label: '' },
            ]}
          >
            {details.ActiveBookings.map((booking) => (
              <tr key={booking.BookingId}>
                <td>{booking.UserName || booking.LoginId}</td>
                <td>{booking.PhoneNumber}</td>
                <td>{formatDateTime(booking.StartUtc)}</td>
                <td>{formatDateTime(booking.EndUtc)}</td>
                <td className="compact-table-actions">
                  {details.Access.CanReleaseBookings ? (
                    <button className="table-action-button table-action-danger"
                      type="button" disabled={isBusy}
                      onClick={() => void releaseBooking(
                        booking.BookingId,
                        booking.UserName || booking.LoginId,
                      )}>
                      Release
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </CompactTable>
        ) : (
          <p>No telephones are currently booked for this event.</p>
        )}
      </div>
    </section>
  );
}

function sortTelephones(
  telephones: PortalPhoneChoice[],
  key: PhoneSortKey,
  direction: SortDirection,
): PortalPhoneChoice[] {
  return [...telephones].sort((left, right) => {
    const leftValue = key === 'number' ? left.PhoneNumber : left.Email;
    const rightValue = key === 'number' ? right.PhoneNumber : right.Email;
    const comparison = leftValue.localeCompare(rightValue, undefined, {
      numeric: key === 'number',
      sensitivity: 'base',
    });
    return direction === 'asc' ? comparison : -comparison;
  });
}

function sortIndicator(active: boolean, direction: SortDirection): string {
  if (!active) return '↕';
  return direction === 'asc' ? '↑' : '↓';
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError) return error.message;
  return 'Telephone reservations could not be loaded.';
}
